import React, { useState, useEffect } from 'react';
import SearchMaquinas from '../../components/Search/SearchMaquinas';
import SeleccionaFecha from '../../components/DatePicker/SeleccionaFecha';
import { useStateContext } from '../../contexts/ContextProvider';
import { crearMensaje, notificarUsuario } from '../../services/Util';
import { getMensajeRol } from '../../services/UsuarioSucursal';
import { Toaster, toast } from 'react-hot-toast';
import { crearTicket } from '../../services/Tikets';
import { traspasoEquipo, getCarpetaEquipo } from '../../services/Carpetas';
import { getCentroCosto } from '../../services/CentroCostos';
import { fetchUser } from '../../utils/fetchUser';
import { formatDate } from '../../formatters/date';

const initialEquipo = {
  _id: null,
  codigo: '',
  nombreEquipo: '',
  marca: '',
  modelo: '',
  serie: '',
  patente: '',
};

const TraspasoCC = () => {
  const { currentColor } = useStateContext();
  const [equipo, setEquipo] = useState(initialEquipo);
  const [ccActual, setCcActual] = useState('');
  const [idCentroActual, setIdCentroActual] = useState('');
  const [centros, setCentros] = useState([]);
  const [idCentroNuevo, setIdCentroNuevo] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [observacion, setObservacion] = useState('');
  const [cargando, setCargando] = useState(false);

  const userInfo = fetchUser();
  const inputClass = 'text-[11px] border border-gray-300 p-1.5 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400';

  useEffect(() => {
    getCentroCosto()
      .then(setCentros)
      .catch(() => setCentros([]));
  }, []);

  const handleSeleccionEquipo = async (doc) => {
    if (!doc) return;
    setEquipo({
      _id: doc._id,
      codigo: doc.codigo,
      nombreEquipo: doc.nombreEquipo,
      marca: doc.marca,
      modelo: doc.modelo,
      serie: doc.serie,
      patente: doc.patente,
    });
    setIdCentroNuevo('');
    setIdCentroActual('');
    setCcActual('Cargando...');
    try {
      const data = await getCarpetaEquipo(doc.codigo);
      const vigente = data?.vigente;
      if (vigente?.idCentro) {
        const nombre = vigente.idCentro.nombreProyecto || vigente.idCentro.codigo || '';
        const cod = vigente.idCentro.codigo ? `${vigente.idCentro.codigo} - ` : '';
        setCcActual(`${cod}${nombre}`);
        setIdCentroActual(vigente.idCentro._id || '');
      } else {
        setCcActual('Sin carpeta vigente');
      }
    } catch {
      setCcActual('—');
    }
  };

  const handleSubmit = async () => {
    if (!equipo.codigo) {
      toast.error('Debe seleccionar un equipo.');
      return;
    }
    if (!idCentroNuevo) {
      toast.error('Debe seleccionar el nuevo Centro de Costo.');
      return;
    }
    if (!selectedDate) {
      toast.error('Debe seleccionar una fecha de traspaso.');
      return;
    }

    setCargando(true);
    try {
      const fecha = formatDate(selectedDate);

      // Crear ticket de traspaso
      const ccNuevo = centros.find(c => c._id === idCentroNuevo);
      const ccNuevoLabel = ccNuevo
        ? `${ccNuevo.codigo ? ccNuevo.codigo + ' - ' : ''}${ccNuevo.nombreProyecto || ''}`
        : idCentroNuevo;

      const dataTicket = {
        empresa: userInfo.empresa,
        tipo: 'Logística',
        numTicket: 0,
        requerimiento: `Traspaso ${equipo.codigo}: ${ccActual} → ${ccNuevoLabel}`,
        observacion: observacion || `Traspaso de ${equipo.codigo} desde ${ccActual} hacia ${ccNuevoLabel}`,
        documentosRespaldo: [],
        fechaEntrega: null,
        numOT: '',
        fechaCreacion: new Date().toISOString(),
        idCreador: userInfo.usuario,
        idAsignado: null,
        estado: 'Pendiente',
      };
      const miTicket = await crearTicket(dataTicket);

      // Ejecutar traspaso: cierra carpeta actual + crea solicitud, asignacion, carpeta e implementa
      await traspasoEquipo(equipo.codigo, idCentroNuevo, equipo.marca, equipo.modelo, observacion);

      // Notificar a Logística
      const mUser = await getMensajeRol('Logística');
      const dataMensaje = {
        numTicket: miTicket.numTicket,
        codigo: equipo.codigo,
        serie: equipo.serie,
        patente: equipo.patente,
        fechaEntrega: fecha,
        ccOrigen: ccActual,
        ccDestino: ccNuevoLabel,
      };
      if (mUser) {
        mUser.forEach((element) => {
          const mensaje = crearMensaje(element._id, 'traspasoCC', dataMensaje);
          notificarUsuario(JSON.stringify(mensaje));
        });
      }

      toast.success(`Traspaso realizado. Ticket N° ${miTicket.numTicket}`);
      setEquipo(initialEquipo);
      setIdCentroNuevo('');
      setCcActual('');
      setIdCentroActual('');
      setSelectedDate(null);
      setObservacion('');
    } catch (err) {
      toast.error('Error al realizar el traspaso: ' + (err?.response?.data?.message || err.message));
    } finally {
      setCargando(false);
    }
  };

  const ccNuevoSeleccionado = centros.find((c) => c._id === idCentroNuevo);

  return (
    <>
      <div className="m-2 md:m-10 mt-16 p-2 md:p-4 bg-gray-100 rounded-3xl">
        <div className="container mx-auto p-2 md:p-2">

          {/* Título */}
          <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200 bg-white rounded-t-md shadow-sm">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
              <p className="text-gray-800 font-semibold text-base md:text-lg">Traspaso de Equipo</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mt-2">

            {/* Panel izquierdo — datos del equipo */}
            <div className="md:col-span-3 bg-white p-6 rounded-lg shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex flex-col">
                  <SearchMaquinas
                    estado="Asignado"
                    onValorSeleccionadoChange={handleSeleccionEquipo}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                <div className="flex flex-col">
                  <label className="text-xs font-semibold mb-1">Equipo</label>
                  <input type="text" className={inputClass} disabled value={equipo.codigo} />
                </div>
                <div className="col-span-2 flex flex-col">
                  <label className="text-xs font-semibold mb-1">Descripción</label>
                  <input type="text" className={inputClass} disabled value={equipo.nombreEquipo} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                <div className="flex flex-col">
                  <label className="text-xs font-semibold mb-1">Modelo</label>
                  <input type="text" className={inputClass} disabled value={equipo.modelo} />
                </div>
                <div className="flex flex-col">
                  <label className="text-xs font-semibold mb-1">Marca</label>
                  <input type="text" className={inputClass} disabled value={equipo.marca} />
                </div>
                <div className="flex flex-col">
                  <label className="text-xs font-semibold mb-1">Serie</label>
                  <input type="text" className={inputClass} disabled value={equipo.serie} />
                </div>
                <div className="flex flex-col">
                  <label className="text-xs font-semibold mb-1">Patente</label>
                  <input type="text" className={inputClass} disabled value={equipo.patente} />
                </div>
              </div>

              {/* CC actual y nuevo */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 border-t border-gray-100 pt-4">
                <div className="flex flex-col">
                  <label className="text-xs font-semibold mb-1">Centro de Costo Actual</label>
                  <input
                    type="text"
                    className={`${inputClass} bg-gray-50`}
                    disabled
                    value={ccActual}
                    placeholder="Seleccione un equipo..."
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-xs font-semibold mb-1">Nuevo Centro de Costo</label>
                  <select
                    className={inputClass}
                    value={idCentroNuevo}
                    onChange={(e) => setIdCentroNuevo(e.target.value)}
                  >
                    <option value="">— Seleccionar —</option>
                    {centros.filter((c) => c._id !== idCentroActual).map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.codigo ? `${c.codigo} - ` : ''}{c.nombreProyecto}
                      </option>
                    ))}
                  </select>
                </div>
                {ccNuevoSeleccionado?.nombreMandante && (
                  <div className="flex flex-col">
                    <label className="text-xs font-semibold mb-1">Mandante nuevo CC</label>
                    <input
                      type="text"
                      className={`${inputClass} bg-gray-50`}
                      disabled
                      value={ccNuevoSeleccionado.nombreMandante}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Panel derecho — fecha y acción */}
            <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col gap-4">
              <div className="flex flex-col">
                <label className="text-xs font-semibold mb-1">Fecha Traspaso</label>
                <SeleccionaFecha
                  selectedDate={selectedDate}
                  handleDateChange={setSelectedDate}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs font-semibold mb-1">Obs.</label>
                <textarea
                  className="text-xs border-gray-200 outline-none border-2 p-2 focus:border-gray-300 h-20 max-h-30"
                  value={observacion}
                  onChange={(e) => setObservacion(e.target.value)}
                />
              </div>
              <button
                onClick={handleSubmit}
                disabled={cargando}
                className="bg-blue-600 text-white py-2 px-4 rounded mt-2 disabled:opacity-50"
              >
                {cargando ? 'Procesando...' : 'Traspasar'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Toaster position="top-right" toastOptions={{ style: { zIndex: 9999 } }} />
    </>
  );
};

export default TraspasoCC;
